/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import {
  SlideOverPanel, StatusPill, SecondaryBtn, PrimaryBtn, DestructiveBtn, SegmentedPills,
} from './systemKit';
import {
  confirmLink, relinkToFoodItem, setManualCost, markUnlinked,
  computeMarginAttribution, fetchPriorPrices, fuzzyMatchFoodItems,
} from '../lib/recipeHealth';

// Fetch the signed-in user's email so audit-attribution helpers can record
// who performed each action. Mirrors the pattern in FoodPriceTracker /
// parseGuardrails (acceptParseSuggestion's userEmail param).
function useCurrentUserEmail() {
  const [email, setEmail] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!cancelled) setEmail(data?.user?.email || null);
    })();
    return () => { cancelled = true; };
  }, []);
  return email;
}

// ─── RecipeDrawer ───────────────────────────────────────────────────────────
// Slide-over panel that opens when an operator clicks a menu_item card on
// MenuProfitability or a queue header on Recipe Health. Hosts:
//
//   1. Header — name, category, menu price, plate cost, margin badge
//   2. Margin attribution strip — "Biggest cost driver: X up Y% — adding $Z
//      per plate" (only renders when |delta| > 2% of plate or > $0.05 abs)
//   3. Pricing health summary — "5 ingredients · 3 linked · 2 to review · 0 unlinked"
//   4. Ingredient list — per-row state chip + per-state action buttons,
//      with inline manual-cost input + "Change link" / "Find match" search
//   5. Edit fields — name, category, menu price (matches old EditItemModal)
//   6. Save / Delete
//
// Replaces the legacy <EditItemModal> in MenuProfitability.js. Still does
// the wholesale-replace save flow (delete all + insert all) — preserves
// existing diffing behavior so live-recalc continues to work as today.
//
// See DESIGN_SYSTEM.md §Recipe Health surface for the linkage state machine.

const CATEGORIES = ['Appetizers','Entrees','Sides','Desserts','Beverages','Other'];
const UNITS = ['oz','lb','each','cup','tbsp','tsp','ml','g'];

function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

// Build the pricing-health summary sentence. Rules:
//   - All in one state → collapse to "N ingredients, all <state> [✓]".
//   - Mixed → "N ingredients · A linked · B to review" (only nonzero
//     states; cap at top 3 explicit; remainder as "· X more"). Skip the
//     zero-count states entirely.
function summarySentence(s) {
  const total = s.total || 0;
  const word = total === 1 ? 'ingredient' : 'ingredients';
  if (total === 0) return `0 ${word}`;
  // Single-state collapse — the only nonzero count equals total.
  if (s.linked === total)   return `${total} ${word}, all linked \u2713`;
  if (s.pending === total)  return `${total} ${word}, all pending review`;
  if (s.unlinked === total) return `${total} ${word}, all unpriced`;
  if (s.manual === total)   return `${total} ${word}, all manual cost`;
  // Mixed — list only nonzero states, ordered by importance, cap at 3.
  const parts = [];
  if (s.linked > 0)   parts.push(`${s.linked} linked`);
  if (s.pending > 0)  parts.push(`${s.pending} to review`);
  if (s.unlinked > 0) parts.push(`${s.unlinked} unlinked`);
  if (s.manual > 0)   parts.push(`${s.manual} manual`);
  const visible = parts.slice(0, 3);
  const overflow = parts.length - visible.length;
  return `${total} ${word} \u00B7 ${visible.join(' \u00B7 ')}${overflow > 0 ? ` \u00B7 ${overflow} more` : ''}`;
}
function fmt$Short(n) {
  if (n == null || !isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
function fmtPct(n) { return n != null && isFinite(n) ? `${n.toFixed(0)}%` : '—'; }

// Quantity-conversion helper — duplicates MenuProfitability's convertQty
// so the drawer can recompute unit_cost from a freshly-relinked food_item
// without round-tripping through the parent component. Kept narrow on
// purpose; if you add unit pairs, mirror them in MenuProfitability.js too.
function convertQty(qty, fromUnit, toUnit) {
  if (!fromUnit || !toUnit) return null;
  if (fromUnit === toUnit) return qty;
  const f = fromUnit.toLowerCase(), t = toUnit.toLowerCase();
  const pairs = {
    'oz_lb': 1 / 16, 'lb_oz': 16,
    'g_lb': 0.00220462, 'lb_g': 453.592,
    'oz_g': 28.3495, 'g_oz': 1 / 28.3495,
    'ml_oz': 1 / 29.5735, 'oz_ml': 29.5735,
    'tbsp_oz': 0.5, 'oz_tbsp': 2,
    'tsp_oz': 1 / 6, 'oz_tsp': 6,
    'cup_oz': 8, 'oz_cup': 1 / 8,
  };
  const k = `${f}_${t}`;
  return pairs[k] != null ? qty * pairs[k] : null;
}

// Compute the recipe-unit-denominated cost from a food_item, mirroring the
// rule used in MenuProfitability.refreshIngredientCost.
function deriveUnitCostFromFoodItem(foodItem, recipeUnit) {
  if (!foodItem) return 0;
  let foodUnit = foodItem.unit || 'each';
  let costPerUnit = Number(foodItem.current_price) || 0;
  if (foodItem.price_per_lb != null && (foodUnit.toLowerCase() === 'case' || foodUnit.toLowerCase() === 'cs')) {
    costPerUnit = Number(foodItem.price_per_lb);
    foodUnit = 'lb';
  }
  const conv = convertQty(1, recipeUnit, foodUnit);
  return conv != null ? costPerUnit * conv : 0;
}

export default function RecipeDrawer({
  open, onClose, companyId, item, foodItems, onSaved, scrollToFirstPending = false,
  // onToast (optional): caller passes setToast so success/failure messages
  // surface in the parent's <Toast> render. Both MenuProfitability and
  // RecipeHealth wire this through. If omitted, transitions still happen
  // but stay silent — degrades gracefully.
  onToast,
}) {
  const userEmail = useCurrentUserEmail();
  const isNew = !item || !item.id;
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Entrees');
  const [menuPrice, setMenuPrice] = useState('');
  const [ings, setIngs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Per-row UI: which row is in "change link" search mode, manual-cost
  // input value, etc. Stored by ingredient temp id.
  const [linkSearchOpenIdx, setLinkSearchOpenIdx] = useState(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [manualCostOpenIdx, setManualCostOpenIdx] = useState(null);
  const [manualCostInput, setManualCostInput] = useState('');
  // Margin attribution strip
  const [priorPrices, setPriorPrices] = useState({});

  // Reset form whenever the drawer opens for a different item.
  useEffect(() => {
    if (!open || !item) return;
    setName(item.name || '');
    setCategory(item.category || 'Entrees');
    setMenuPrice(item.menu_price != null ? String(item.menu_price) : '');
    setIngs((item.ingredients || []).map(i => ({
      // Each row carries its DB id (or null for new rows) as the durable
      // identifier. The drawer mutates these via the recipeHealth helpers
      // (which write to DB directly) — we re-fetch on every operation
      // through onSaved so the parent + drawer stay in sync.
      id: i.id || null,
      ingredient_name: i.ingredient_name || '',
      quantity: i.quantity != null ? String(i.quantity) : '',
      unit: i.unit || 'oz',
      unit_cost: i.unit_cost || 0,
      line_cost: i.line_cost || 0,
      food_item_id: i.food_item_id || null,
      link_status: i.link_status || 'pending_review',
      manual_unit_cost: i.manual_unit_cost,
      match_confidence: i.match_confidence,
      // is_ai_generated stays as historical provenance — see recipeHealth.js
      // header comment. We never modify this from the drawer.
      is_ai_generated: i.is_ai_generated,
    })));
    setLinkSearchOpenIdx(null);
    setManualCostOpenIdx(null);
    setError('');
  }, [open, item?.id]);

  // Fetch trailing-30d prior prices for the linked ingredients (used by
  // the margin attribution strip).
  useEffect(() => {
    if (!open || !item || isNew) { setPriorPrices({}); return; }
    const linkedIds = (item.ingredients || [])
      .filter(i => i.link_status === 'linked' && i.food_item_id)
      .map(i => i.food_item_id);
    if (linkedIds.length === 0) { setPriorPrices({}); return; }
    let cancelled = false;
    (async () => {
      const map = await fetchPriorPrices({ companyId, foodItemIds: linkedIds });
      if (!cancelled) setPriorPrices(map);
    })();
    return () => { cancelled = true; };
  }, [open, item?.id, isNew, companyId]);

  // Scroll-to-first-pending when chip click opens drawer. One-shot via
  // the scrollToFirstPending prop — caller passes true only when entering
  // from a pricing_health chip click.
  useEffect(() => {
    if (!open || !scrollToFirstPending || ings.length === 0) return;
    const t = setTimeout(() => {
      const firstPendingIdx = ings.findIndex(i => i.link_status === 'pending_review');
      if (firstPendingIdx === -1) return;
      const el = document.querySelector(`[data-recipe-ingredient-row="${firstPendingIdx}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250); // wait for drawer slide-in to settle
    return () => clearTimeout(t);
  }, [open, scrollToFirstPending, ings.length]);

  // ── Live derived state ─────────────────────────────────────────────────
  // Plate cost / margin / pricing health summary are computed from the
  // current `ings` array so they update immediately as the operator edits.
  // Persisted values (menu_items.plate_cost, .margin_pct, .pricing_health)
  // are recomputed on save via the helpers in recipeHealth.js.
  const plateCost = useMemo(() => {
    return ings.reduce((s, i) => {
      // unlinked_no_cost contributes 0 — its unit_cost should already be 0
      // but be defensive in case the row is mid-edit.
      if (i.link_status === 'unlinked_no_cost') return s;
      return s + (Number(i.quantity) || 0) * (Number(i.unit_cost) || 0);
    }, 0);
  }, [ings]);
  const marginPct = useMemo(() => {
    const price = parseFloat(menuPrice);
    if (!price || price <= 0) return null;
    return ((price - plateCost) / price) * 100;
  }, [menuPrice, plateCost]);
  const summary = useMemo(() => {
    const total = ings.length;
    const linked = ings.filter(i => i.link_status === 'linked').length;
    const pending = ings.filter(i => i.link_status === 'pending_review').length;
    const manual = ings.filter(i => i.link_status === 'unlinked_manual_cost').length;
    const unlinked = ings.filter(i => i.link_status === 'unlinked_no_cost').length;
    return { total, linked, pending, manual, unlinked };
  }, [ings]);

  const attribution = useMemo(() => {
    return computeMarginAttribution({ ingredients: ings, foodItemPriorPrices: priorPrices, plateCost });
  }, [ings, priorPrices, plateCost]);

  // ── Per-row helpers ────────────────────────────────────────────────────
  function updateIng(idx, patch) {
    setIngs(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
  }

  // Apply a state-machine helper, then reflect the new row state in the
  // drawer's local list. Persistence happens via the helper (writes to DB
  // + appends audit log + refreshes parent menu_item.pricing_health).
  // Errors surface as a critical-tone toast.
  async function applyTransition(idx, helperPromise, successToast) {
    try {
      const updated = await helperPromise;
      if (updated) updateIng(idx, {
        food_item_id: updated.food_item_id,
        link_status: updated.link_status,
        unit_cost: updated.unit_cost,
        line_cost: updated.line_cost,
        manual_unit_cost: updated.manual_unit_cost,
        match_confidence: updated.match_confidence,
      });
      if (successToast) onToast?.(successToast);
      onSaved?.(); // refresh parent so plate_cost / margin update
    } catch (err) {
      onToast?.({ kind: 'critical', text: `Action failed: ${err.message || 'unknown error'}` });
    }
  }

  async function handleConfirmLink(idx) {
    const ing = ings[idx];
    if (!ing.id) return; // can't confirm an unsaved row
    if (!userEmail) { onToast?.({ kind: 'warning', text: 'Sign in to confirm linkages.' }); return; }
    const linkedFi = foodItems.find(f => f.id === ing.food_item_id);
    const successToast = {
      kind: 'success',
      text: linkedFi ? `Linked to ${linkedFi.normalized_name}` : 'Link confirmed',
    };
    await applyTransition(idx, confirmLink({ companyId, recipeIngredientId: ing.id, userEmail }), successToast);
  }

  async function handleRelink(idx, foodItem, matchScore) {
    const ing = ings[idx];
    if (!ing.id) {
      // Unsaved row — just update local state; save flow will persist.
      updateIng(idx, {
        food_item_id: foodItem.id,
        unit_cost: deriveUnitCostFromFoodItem(foodItem, ing.unit),
        link_status: 'linked',
      });
      setLinkSearchOpenIdx(null);
      setLinkSearchQuery('');
      return;
    }
    if (!userEmail) { onToast?.({ kind: 'warning', text: 'Sign in to update linkages.' }); return; }
    const newUnitCost = deriveUnitCostFromFoodItem(foodItem, ing.unit);
    const successToast = {
      kind: 'success',
      text: `Re-linked to ${foodItem.normalized_name}`,
    };
    await applyTransition(idx, relinkToFoodItem({
      companyId,
      recipeIngredientId: ing.id,
      foodItemId: foodItem.id,
      newUnitCost,
      matchConfidence: matchScore || null,
      userEmail,
    }), successToast);
    setLinkSearchOpenIdx(null);
    setLinkSearchQuery('');
  }

  async function handleSetManualCost(idx) {
    const ing = ings[idx];
    const value = parseFloat(manualCostInput);
    if (!isFinite(value) || value < 0) return;
    if (!ing.id) {
      // Unsaved row — just update local state; save flow will persist.
      updateIng(idx, {
        link_status: 'unlinked_manual_cost',
        food_item_id: null,
        unit_cost: value,
        manual_unit_cost: value,
      });
      setManualCostOpenIdx(null);
      setManualCostInput('');
      return;
    }
    if (!userEmail) { onToast?.({ kind: 'warning', text: 'Sign in to set manual costs.' }); return; }
    const successToast = {
      kind: 'success',
      text: `Manual cost set: $${value.toFixed(2)}/${ing.unit}`,
    };
    await applyTransition(idx, setManualCost({
      companyId,
      recipeIngredientId: ing.id,
      manualUnitCost: value,
      userEmail,
    }), successToast);
    setManualCostOpenIdx(null);
    setManualCostInput('');
  }

  async function handleMarkUnlinked(idx) {
    const ing = ings[idx];
    if (!ing.id) {
      updateIng(idx, {
        link_status: 'unlinked_no_cost',
        food_item_id: null,
        unit_cost: 0,
        manual_unit_cost: null,
      });
      return;
    }
    if (!userEmail) { onToast?.({ kind: 'warning', text: 'Sign in to update linkages.' }); return; }
    const successToast = {
      kind: 'success',
      text: 'Marked unpriced — 1 ingredient excluded from plate cost',
    };
    await applyTransition(idx, markUnlinked({ companyId, recipeIngredientId: ing.id, userEmail }), successToast);
  }

  function addRow() {
    setIngs(prev => [...prev, {
      id: null,
      ingredient_name: '',
      quantity: '',
      unit: 'oz',
      unit_cost: 0,
      line_cost: 0,
      food_item_id: null,
      link_status: 'pending_review',
      manual_unit_cost: null,
      match_confidence: null,
      is_ai_generated: false,
    }]);
  }
  function removeRow(idx) {
    setIngs(prev => prev.filter((_, i) => i !== idx));
  }

  // ── Save (name / category / price / wholesale-replace ingredients) ────
  // Linkage state mutations have already been persisted by the helpers as
  // the operator clicked. This save handles the other fields (name,
  // category, price) AND new/edited rows the operator added without
  // hitting a state-machine action button.
  async function save() {
    if (!name.trim()) { setError('Dish name is required.'); return; }
    const price = parseFloat(menuPrice);
    if (!isFinite(price) || price <= 0) { setError('Menu price must be greater than 0.'); return; }
    setSaving(true); setError('');
    try {
      let menuItemId = item?.id;
      if (isNew) {
        const { data, error: insErr } = await supabase.from('menu_items').insert({
          company_id: companyId,
          name: name.trim(),
          category,
          menu_price: price,
          plate_cost: plateCost,
          margin_pct: marginPct,
          is_active: true,
          pricing_health: 'no_recipe', // refreshed below after ingredients land
        }).select().single();
        if (insErr) throw insErr;
        menuItemId = data.id;
      } else {
        await supabase.from('menu_items').update({
          name: name.trim(), category, menu_price: price,
          plate_cost: plateCost, margin_pct: marginPct,
          updated_at: new Date().toISOString(),
        }).eq('id', menuItemId);
      }

      // Wholesale-replace ingredients (matches the legacy save behavior).
      // Linkage state from the local list is preserved in the new rows so
      // the operator's confirmations / manual costs survive a Save.
      await supabase.from('recipe_ingredients').delete().eq('menu_item_id', menuItemId);
      const valid = ings.filter(i => i.ingredient_name.trim() && Number(i.quantity) > 0);
      if (valid.length > 0) {
        await supabase.from('recipe_ingredients').insert(valid.map(i => ({
          menu_item_id: menuItemId,
          company_id: companyId,
          food_item_id: i.food_item_id,
          ingredient_name: i.ingredient_name.trim(),
          quantity: Number(i.quantity),
          unit: i.unit,
          unit_cost: Number(i.unit_cost) || 0,
          line_cost: Number(i.quantity) * (Number(i.unit_cost) || 0),
          is_ai_generated: !!i.is_ai_generated,
          link_status: i.link_status,
          manual_unit_cost: i.link_status === 'unlinked_manual_cost' ? (Number(i.manual_unit_cost) || Number(i.unit_cost) || 0) : null,
          match_confidence: i.match_confidence ?? null,
          linked_at: i.link_status === 'linked' ? new Date().toISOString() : null,
        })));
      }
      // Recompute menu_item.pricing_health based on persisted ingredient
      // states. computePricingHealth is mirrored in SQL on the migration
      // and in JS here — keep them in sync.
      const newHealth =
        valid.length === 0 ? 'no_recipe'
        : valid.some(i => i.link_status === 'pending_review') ? 'has_pending_review'
        : valid.some(i => i.link_status === 'unlinked_no_cost') ? 'has_unlinked'
        : 'healthy';
      await supabase.from('menu_items').update({ pricing_health: newHealth }).eq('id', menuItemId);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
    setSaving(false);
  }

  async function deleteItem() {
    if (!item?.id) { onClose(); return; }
    if (!window.confirm('Delete this menu item? Ingredients will also be removed.')) return;
    await supabase.from('menu_items').update({ is_active: false }).eq('id', item.id);
    onSaved?.();
    onClose?.();
  }

  if (!item && !isNew) return null;

  const subtitle = (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <span>{category}</span>
      {!isNew && menuPrice && <span style={{ color: 'var(--text-muted)' }}>{'\u00B7'}</span>}
      {!isNew && menuPrice && <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(parseFloat(menuPrice))}</span>}
    </span>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={isNew ? 'New menu item' : (name || 'Untitled')}
      subtitle={isNew ? null : subtitle}
      widthPx={580}
    >
      <div className="p-5 space-y-5">
        {/* ── Header strip — plate cost + margin ─────────────────────────── */}
        {!isNew && (
          <div
            className="rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap"
            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Plate cost</div>
              <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(plateCost)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Margin</div>
              <div className="text-lg font-semibold" style={{
                color: marginPct == null ? 'var(--text-muted)' : marginPct >= 65 ? 'var(--text-success)' : marginPct >= 55 ? 'var(--text-warning)' : 'var(--text-critical)',
                fontVariantNumeric: 'tabular-nums',
              }}>{fmtPct(marginPct)}</div>
              {summary.unlinked > 0 && (
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-warning)' }}>
                  {`Estimate excludes ${summary.unlinked} unpriced ingredient${summary.unlinked === 1 ? '' : 's'}`}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Margin attribution strip ──────────────────────────────────── */}
        {attribution && (
          <div
            className="rounded-lg p-3"
            style={{
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${attribution.direction === 'up' ? 'var(--text-critical)' : 'var(--text-success)'}`,
            }}
          >
            <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.5, fontWeight: 500 }}>
              {attribution.direction === 'up'
                ? <>Biggest cost driver this month: <span style={{ color: 'var(--accent-primary)' }}>{attribution.ingredient.ingredient_name}</span> up {((attribution.currentPerUnit / attribution.priorPerUnit - 1) * 100).toFixed(0)}% — adding {fmt$(Math.abs(attribution.deltaPerPlate))} per plate</>
                : <>Biggest savings this month: <span style={{ color: 'var(--accent-primary)' }}>{attribution.ingredient.ingredient_name}</span> down {((1 - attribution.currentPerUnit / attribution.priorPerUnit) * 100).toFixed(0)}% — saving {fmt$(Math.abs(attribution.deltaPerPlate))} per plate</>
              }
            </div>
          </div>
        )}

        {/* ── Pricing health summary ─────────────────────────────────────── */}
        {/* Conditional rendering: show single-state collapse copy when one
            state dominates; otherwise show only nonzero state breakdowns
            (cap at 3 explicit + "X more" overflow). See DESIGN_SYSTEM.md
            §Conditional-stat rendering. */}
        {!isNew && summary.total > 0 && (
          <div data-recipe-ingredients-section className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {summarySentence(summary)}
          </div>
        )}

        {/* ── Edit fields (name / category / price) ─────────────────────── */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
            Dish details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs block mb-1" style={{ color: 'var(--text-secondary)' }}>Name</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: 'var(--text-secondary)' }}>Menu price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-muted)' }}>$</span>
                <input
                  type="number" step="0.01" value={menuPrice} onChange={e => setMenuPrice(e.target.value)}
                  className="w-full rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs block mb-1" style={{ color: 'var(--text-secondary)' }}>Category</label>
            <select
              value={category} onChange={e => setCategory(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* ── Ingredient list with state machine ────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
              Ingredients
            </div>
            <SecondaryBtn onClick={addRow} className="!text-xs !px-3 !py-1.5">+ Add ingredient</SecondaryBtn>
          </div>
          {ings.length === 0 ? (
            <div className="rounded-lg p-4 text-sm text-center" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
              No ingredients yet. Add at least one to calculate margin.
            </div>
          ) : (
            <div className="space-y-2">
              {ings.map((ing, idx) => (
                <IngredientRow
                  key={ing.id || `new-${idx}`}
                  idx={idx}
                  ing={ing}
                  foodItems={foodItems}
                  linkSearchOpen={linkSearchOpenIdx === idx}
                  linkSearchQuery={linkSearchQuery}
                  manualCostOpen={manualCostOpenIdx === idx}
                  manualCostInput={manualCostInput}
                  onUpdate={(patch) => updateIng(idx, patch)}
                  onRemove={() => removeRow(idx)}
                  onConfirmLink={() => handleConfirmLink(idx)}
                  onOpenLinkSearch={() => { setLinkSearchOpenIdx(idx); setLinkSearchQuery(ing.ingredient_name || ''); }}
                  onCloseLinkSearch={() => { setLinkSearchOpenIdx(null); setLinkSearchQuery(''); }}
                  onLinkSearchQueryChange={setLinkSearchQuery}
                  onPickFoodItem={(fi, score) => handleRelink(idx, fi, score)}
                  onOpenManualCost={() => { setManualCostOpenIdx(idx); setManualCostInput(ing.unit_cost ? String(ing.unit_cost) : ''); }}
                  onCloseManualCost={() => { setManualCostOpenIdx(null); setManualCostInput(''); }}
                  onManualCostInputChange={setManualCostInput}
                  onSaveManualCost={() => handleSetManualCost(idx)}
                  onMarkUnlinked={() => handleMarkUnlinked(idx)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Save / Delete ─────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-lg p-3 text-sm" style={{ background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)', border: '1px solid var(--border)' }}>
            {error}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap pt-2" style={{ borderTop: '1px solid var(--border)' }}>
          <div>
            {!isNew && (
              <DestructiveBtn onClick={deleteItem} className="!text-sm">Delete</DestructiveBtn>
            )}
          </div>
          <div className="flex items-center gap-2">
            <SecondaryBtn onClick={onClose} className="!text-sm">Cancel</SecondaryBtn>
            <PrimaryBtn onClick={save} disabled={saving} className="!text-sm">
              {saving ? 'Saving\u2026' : (isNew ? 'Create' : 'Save')}
            </PrimaryBtn>
          </div>
        </div>
      </div>
    </SlideOverPanel>
  );
}

// ─── IngredientRow ──────────────────────────────────────────────────────────
// One row per ingredient. Renders state chip + per-state action buttons.
// Inline expanding zones: link search + manual cost input.
function IngredientRow({
  idx, ing, foodItems,
  linkSearchOpen, linkSearchQuery, onOpenLinkSearch, onCloseLinkSearch, onLinkSearchQueryChange, onPickFoodItem,
  manualCostOpen, manualCostInput, onOpenManualCost, onCloseManualCost, onManualCostInputChange, onSaveManualCost,
  onUpdate, onRemove, onConfirmLink, onMarkUnlinked,
}) {
  const linkedFoodItem = useMemo(
    () => ing.food_item_id ? foodItems.find(f => f.id === ing.food_item_id) : null,
    [ing.food_item_id, foodItems]
  );

  // ── State chip ─────────────────────────────────────────────────────────
  const stateChip = (() => {
    switch (ing.link_status) {
      case 'linked':                return <StatusPill tone="green">Linked</StatusPill>;
      case 'pending_review':        return <StatusPill tone="amber">Pending review</StatusPill>;
      case 'unlinked_manual_cost':  return <StatusPill tone="neutral">Manual cost</StatusPill>;
      case 'unlinked_no_cost':      return <StatusPill tone="amber">Unlinked</StatusPill>;
      default:                      return null;
    }
  })();

  return (
    <div
      data-recipe-ingredient-row={idx}
      className="rounded-lg p-3"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      {/* Row 1: name + qty/unit + cost */}
      <div className="grid items-center gap-2" style={{ gridTemplateColumns: '1fr 60px 70px 90px 24px' }}>
        <input
          type="text"
          value={ing.ingredient_name}
          onChange={e => onUpdate({ ingredient_name: e.target.value })}
          placeholder="Ingredient name"
          className="text-sm rounded-lg px-2 py-1.5 focus:outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
        />
        <input
          type="number" step="0.01" value={ing.quantity}
          onChange={e => onUpdate({ quantity: e.target.value })}
          placeholder="0"
          className="text-sm rounded-lg px-2 py-1.5 focus:outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        />
        <select
          value={ing.unit}
          onChange={e => onUpdate({ unit: e.target.value })}
          className="text-sm rounded-lg px-2 py-1.5 focus:outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
        >
          {UNITS.map(u => <option key={u}>{u}</option>)}
        </select>
        <div className="text-sm font-semibold" style={{
          color: ing.link_status === 'unlinked_no_cost' ? 'var(--text-muted)' : 'var(--text-primary)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {ing.link_status === 'unlinked_no_cost' ? '\u2014' : fmt$(Number(ing.unit_cost || 0) * Number(ing.quantity || 0))}
        </div>
        <button
          onClick={onRemove}
          aria-label="Remove ingredient"
          className="text-sm"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, lineHeight: 1 }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-critical)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
        >{'\u2715'}</button>
      </div>

      {/* Row 2: state chip + linked food item name + action buttons */}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {stateChip}
        {linkedFoodItem && (ing.link_status === 'linked' || ing.link_status === 'pending_review') && (
          <span className="text-xs truncate" style={{ color: 'var(--text-secondary)', maxWidth: 280 }} title={linkedFoodItem.normalized_name}>
            {`\u2192 ${linkedFoodItem.normalized_name}`}
          </span>
        )}
        {linkedFoodItem?.needs_review && (
          <StatusPill tone="amber">Linked item has data review flag</StatusPill>
        )}
        {ing.is_ai_generated && ing.link_status === 'pending_review' && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>AI-suggested</span>
        )}

        {/* Action buttons depend on link_status */}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {ing.link_status === 'pending_review' && (
            <>
              {ing.food_item_id && (
                <PrimaryBtn onClick={onConfirmLink} className="!text-xs !px-2.5 !py-1">Confirm link</PrimaryBtn>
              )}
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">{ing.food_item_id ? 'Change link' : 'Find match'}</SecondaryBtn>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Manual cost</SecondaryBtn>
              <SecondaryBtn onClick={onMarkUnlinked} className="!text-xs !px-2.5 !py-1">Leave unpriced</SecondaryBtn>
            </>
          )}
          {ing.link_status === 'linked' && (
            <>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Change link</SecondaryBtn>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Manual cost</SecondaryBtn>
              <SecondaryBtn onClick={onMarkUnlinked} className="!text-xs !px-2.5 !py-1">Mark unlinked</SecondaryBtn>
            </>
          )}
          {ing.link_status === 'unlinked_no_cost' && (
            <>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Find match</SecondaryBtn>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Set manual cost</SecondaryBtn>
            </>
          )}
          {ing.link_status === 'unlinked_manual_cost' && (
            <>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Edit cost</SecondaryBtn>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Find match in catalog</SecondaryBtn>
            </>
          )}
        </div>
      </div>

      {/* Inline link search */}
      {linkSearchOpen && (
        <LinkSearch
          query={linkSearchQuery}
          onQueryChange={onLinkSearchQueryChange}
          foodItems={foodItems}
          ingredientName={ing.ingredient_name}
          onPick={onPickFoodItem}
          onClose={onCloseLinkSearch}
        />
      )}

      {/* Inline manual cost input */}
      {manualCostOpen && (
        <div className="mt-2 rounded-lg p-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>{`Cost per ${ing.unit}`}</label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-muted)' }}>$</span>
              <input
                type="number" step="0.01" value={manualCostInput}
                onChange={e => onManualCostInputChange(e.target.value)}
                autoFocus
                className="rounded-lg pl-6 pr-2 py-1.5 text-sm focus:outline-none"
                style={{ width: 110, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
              />
            </div>
            <PrimaryBtn onClick={onSaveManualCost} className="!text-xs !px-3 !py-1.5">Save cost</PrimaryBtn>
            <SecondaryBtn onClick={onCloseManualCost} className="!text-xs !px-3 !py-1.5">Cancel</SecondaryBtn>
          </div>
          <div className="text-xs mt-2" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
            This won't auto-update when invoice prices change. You can re-link to a catalog item later.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LinkSearch ─────────────────────────────────────────────────────────────
// Inline searchable dropdown for picking a food_item from the catalog.
// Top 5 fuzzy matches surfaced first; "Browse all" mode shows the full
// alphabetical catalog. Confidence scoring is intentionally lightweight in
// v1 — the menu-upload-time confirmation step in Commit 2 will introduce
// the real scorer.
function LinkSearch({ query, onQueryChange, foodItems, ingredientName, onPick, onClose }) {
  const [browseAll, setBrowseAll] = useState(false);
  const matches = useMemo(() => {
    if (browseAll) {
      return foodItems
        .slice()
        .sort((a, b) => (a.normalized_name || '').localeCompare(b.normalized_name || ''))
        .slice(0, 200);
    }
    const initial = query || ingredientName || '';
    return fuzzyMatchFoodItems(initial, foodItems, 5);
  }, [browseAll, query, ingredientName, foodItems]);

  return (
    <div className="mt-2 rounded-lg p-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          type="text" value={query} onChange={e => onQueryChange(e.target.value)}
          placeholder={ingredientName ? `Searching: ${ingredientName}` : 'Search catalog'}
          autoFocus
          className="flex-1 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
        />
        <SegmentedPills
          value={browseAll ? 'browse' : 'top5'}
          onChange={(v) => setBrowseAll(v === 'browse')}
          options={[
            { key: 'top5', label: 'Top matches' },
            { key: 'browse', label: 'Browse all' },
          ]}
          size="sm"
        />
        <SecondaryBtn onClick={onClose} className="!text-xs !px-2.5 !py-1">Cancel</SecondaryBtn>
      </div>
      <div
        className="rounded-lg overflow-y-auto"
        style={{ maxHeight: 240, background: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        {matches.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            {query ? 'No catalog items match.' : 'Start typing to search.'}
          </div>
        ) : (
          matches.map((fi, i) => (
            <div
              key={fi.id}
              onClick={() => onPick(fi, fi._matchScore)}
              className="px-3 py-2 cursor-pointer"
              style={{
                borderBottom: i < matches.length - 1 ? '1px solid var(--border)' : 'none',
                color: 'var(--text-primary)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{fi.normalized_name}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {fi.category || 'Uncategorized'}
                    {fi.is_catch_weight && ' \u00B7 catch-weight'}
                    {fi.current_price != null && ` \u00B7 ${fmt$Short(fi.current_price)}/${fi.unit || 'unit'}`}
                  </div>
                </div>
                {!browseAll && fi._matchScore != null && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(fi._matchScore * 100)}%
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
